<template>
    <v-card class="background-2 round-card" flat>
        <v-card-text class="pa-3">

            <!-- Year navigation -->
            <div class="d-flex align-center justify-space-between mb-3">
                <v-btn icon small @click="prevYear">
                    <v-icon>mdi-chevron-left</v-icon>
                </v-btn>
                <span class="subtitle-1 font-weight-medium">{{ selectedYear }}</span>
                <v-btn icon small @click="nextYear">
                    <v-icon>mdi-chevron-right</v-icon>
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn
                    color="primary"
                    small
                    @click="openAdd"
                >
                    <v-icon left small>mdi-plus</v-icon>
                    Aggiungi Eccezione
                </v-btn>
            </div>

            <!-- Exceptions table -->
            <v-data-table
                :headers="headers"
                :items="exceptionsForYear"
                :items-per-page="10"
                class="background-2"
                no-data-text="Nessuna eccezione per questo anno"
                dense
            >
                <template v-slot:item.date="{ item }">
                    {{ formatDate(item.date) }}
                </template>

                <template v-slot:item.type="{ item }">
                    <v-chip
                        x-small
                        :color="item.is_holiday ? 'error' : 'orange'"
                        dark
                    >
                        {{ item.is_holiday ? 'Festivo' : 'Orario Speciale' }}
                    </v-chip>
                </template>

                <template v-slot:item.orario="{ item }">
                    <span v-if="!item.is_holiday && item.start_time && item.end_time">
                        {{ item.start_time }} – {{ item.end_time }}
                    </span>
                    <span v-else class="grey--text">—</span>
                </template>

                <template v-slot:item.actions="{ item }">
                    <v-btn icon x-small @click="openEdit(item)">
                        <v-icon small>mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn icon x-small color="error" @click="removeException(item)">
                        <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                </template>
            </v-data-table>

        </v-card-text>

        <!-- Add/Edit dialog -->
        <v-dialog v-model="dialog" max-width="500" persistent>
            <v-card class="round-card">
                <v-card-title>
                    <v-icon color="primary" class="mr-2">mdi-calendar-alert</v-icon>
                    {{ editingIndex !== null ? 'Modifica Eccezione' : 'Aggiungi Eccezione' }}
                </v-card-title>
                <v-divider></v-divider>

                <v-card-text class="pt-4">
                    <v-row>
                        <v-col cols="12" sm="6">
                            <gmh-date-picker
                                v-model="exceptionForm.date"
                                label="Data"
                                hide-details
                                editable
                            ></gmh-date-picker>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field
                                v-model="exceptionForm.name"
                                label="Nome"
                                outlined
                                dense
                                hide-details="auto"
                                placeholder="es. Natale"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="12">
                            <v-radio-group
                                v-model="exceptionForm.is_holiday"
                                row
                                hide-details
                                class="mt-0"
                                label="Tipo"
                            >
                                <v-radio
                                    :value="true"
                                    label="Festivo (chiuso)"
                                    color="error"
                                ></v-radio>
                                <v-radio
                                    :value="false"
                                    label="Orario speciale"
                                    color="orange"
                                ></v-radio>
                            </v-radio-group>
                        </v-col>

                        <template v-if="exceptionForm.is_holiday === false">
                            <v-col cols="6">
                                <v-text-field
                                    v-model="exceptionForm.start_time"
                                    label="Inizio"
                                    type="time"
                                    outlined
                                    dense
                                    hide-details="auto"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="6">
                                <v-text-field
                                    v-model="exceptionForm.end_time"
                                    label="Fine"
                                    type="time"
                                    outlined
                                    dense
                                    hide-details="auto"
                                ></v-text-field>
                            </v-col>
                        </template>
                    </v-row>
                </v-card-text>

                <v-divider></v-divider>
                <v-card-actions class="pa-3">
                    <v-spacer></v-spacer>
                    <v-btn text @click="closeDialog">Annulla</v-btn>
                    <v-btn color="primary" @click="saveException">Salva</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

    </v-card>
</template>

<script>
// Props: value — array of exception objects
// Emits: input (v-model pattern)
//
// Usage:
//   <working-hours-exceptions v-model="form.exceptions"></working-hours-exceptions>

function emptyForm() {
    return {
        date: '',
        name: '',
        is_holiday: true,
        start_time: '09:00',
        end_time: '18:00'
    }
}

export default {
    name: 'WorkingHoursExceptions',

    props: {
        value: {
            type: Array,
            default: () => []
        }
    },

    data() {
        return {
            selectedYear: new Date().getFullYear(),
            dialog: false,
            editingIndex: null,
            exceptionForm: emptyForm(),
            headers: [
                { text: 'Data', value: 'date', sortable: true },
                { text: 'Nome', value: 'name', sortable: true },
                { text: 'Tipo', value: 'type', sortable: false },
                { text: 'Orario', value: 'orario', sortable: false },
                { text: 'Azioni', value: 'actions', sortable: false, align: 'end' }
            ]
        }
    },

    computed: {
        exceptionsForYear() {
            return (this.value || [])
                .map((ex, idx) => ({ ...ex, _idx: idx }))
                .filter(ex => {
                    if (!ex.date) return false
                    const year = parseInt(ex.date.split('-')[0])
                    return year === this.selectedYear
                })
                .sort((a, b) => a.date.localeCompare(b.date))
        }
    },

    methods: {
        prevYear() {
            this.selectedYear--
        },

        nextYear() {
            this.selectedYear++
        },

        formatDate(dateStr) {
            if (!dateStr) return ''
            const [y, m, d] = dateStr.split('-')
            return `${d}/${m}/${y}`
        },

        openAdd() {
            this.editingIndex = null
            this.exceptionForm = emptyForm()
            // Default date to first day of selected year
            this.exceptionForm.date = `${this.selectedYear}-01-01`
            this.dialog = true
        },

        openEdit(item) {
            this.editingIndex = item._idx
            this.exceptionForm = {
                date: item.date || '',
                name: item.name || '',
                is_holiday: item.is_holiday !== undefined ? item.is_holiday : true,
                start_time: item.start_time || '09:00',
                end_time: item.end_time || '18:00'
            }
            this.dialog = true
        },

        closeDialog() {
            this.dialog = false
            this.editingIndex = null
            this.exceptionForm = emptyForm()
        },

        saveException() {
            if (!this.exceptionForm.date) {
                return
            }
            const exceptions = [...(this.value || [])]
            const entry = {
                date: this.exceptionForm.date,
                name: this.exceptionForm.name,
                is_holiday: this.exceptionForm.is_holiday
            }
            if (!this.exceptionForm.is_holiday) {
                entry.start_time = this.exceptionForm.start_time
                entry.end_time = this.exceptionForm.end_time
            }

            if (this.editingIndex !== null) {
                exceptions.splice(this.editingIndex, 1, entry)
            } else {
                exceptions.push(entry)
            }
            this.$emit('input', exceptions)
            this.closeDialog()
        },

        removeException(item) {
            const exceptions = [...(this.value || [])]
            exceptions.splice(item._idx, 1)
            this.$emit('input', exceptions)
        }
    }
}
</script>
